import React, { useContext, useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "../assets/Vyango.png";
import { AuthContext } from "../context/AuthContext";
import NotificationList from "./NotificationList";
// ✅ Import central API tools
import { getUnreadNotificationCount, SERVER_URL } from "../utils/api";
import "../css/navbar.css";

export default function Navbar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const ctx = useContext(AuthContext) || { user: null, clearUser: () => {}, token: null };

  const { user } = ctx;

  // ✅ FIX 1: Robust ID detection (Crucial for syncing after profile updates)
  const userId = user?._id || user?.id;

  const [currentUser, setCurrentUser] = useState(user);
  const [imgError, setImgError] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [searchText, setSearchText] = useState("");

  const notificationRefDesktop = useRef(null);
  const notificationRefMobile = useRef(null);

  // ✅ Cleanly format the avatar URL with your dynamic IP
  let avatarUrl = currentUser?.avatar || currentUser?.image || null;
  if (avatarUrl && !avatarUrl.startsWith("http")) {
    avatarUrl = `${SERVER_URL}${avatarUrl}`;
  }

  useEffect(() => {
    setCurrentUser(user || null);
    setImgError(false);
  }, [user]);

  // ✅ FIX 2: Fixed the fetch logic to use the robust userId
  useEffect(() => {
    if (!userId || !ctx.token) return;

    const fetchUnreadCount = async () => {
      try {
        // Use the normalized userId here
        const count = await getUnreadNotificationCount(userId, ctx.token);
        setUnreadCount(count);
      } catch (err) {
        console.error("Notification fetch error:", err);
      }
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [userId, ctx.token]); // ✅ Added userId as dependency to trigger on profile update

  // Handle clicking outside the notification dropdown
  useEffect(() => {
    function closeIfClickedOutside(e) {
      if (
        (notificationRefDesktop.current && !notificationRefDesktop.current.contains(e.target)) &&
        (notificationRefMobile.current && !notificationRefMobile.current.contains(e.target))
      ) {
        setShowNotifications(false);
      }
    }

    if (showNotifications && !menuOpen) {
      document.addEventListener("mousedown", closeIfClickedOutside);
    }
    return () => document.removeEventListener("mousedown", closeIfClickedOutside);
  }, [showNotifications, menuOpen]);

  // Close menus on page change
  useEffect(() => {
    setMenuOpen(false);
    setShowNotifications(false);
  }, [pathname]);

  const initials = (name) => {
    if (!name) return "?";
    const parts = name.split(" ");
    if (parts.length === 1) return name[0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const toggleMenu = () => setMenuOpen((prev) => !prev);

  const toggleNotifications = () => {
    setShowNotifications((prev) => !prev);
    if (!showNotifications) setUnreadCount(0);
  };

  const handleNotificationClick = (n) => {
    const fromId = n?.from?._id || n?.from?.id || n?.from;
    const rideId = n.rideId || n.ride?._id;

    if (n.messageType === "ride_request" && rideId) {
      navigate(`/ride/${rideId}`);
    } else if (fromId) {
      navigate(`/chat/${fromId}`);
    }
  };

  const handleSearch = (e) => {
    if (e.key === "Enter" && searchText.trim() !== "") {
      navigate(`/search?query=${encodeURIComponent(searchText)}`);
    }
  };

  return (
    <header className="navbar-container">
      <div className="navbar-inner">
        {/* LEFT - LOGO */}
        <div className="nav-left">
          <Link to="/">
            <img src={logo} alt="Logo" className="nav-logo" />
          </Link>
        </div>

        {/* CENTER - SEARCH */}
        <div className="nav-search">
          <input
            type="text"
            placeholder="Search rides..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={handleSearch}
          />
        </div>

        {/* RIGHT - LINKS */}
        <div className="nav-right">
          <Link to="/" className={`nav-link ${pathname === "/" ? "active" : ""}`}>
            Home
          </Link>
          <Link to="/post" className={`nav-link ${pathname === "/post" ? "active" : ""}`}>
            Post Ride
          </Link>
          <Link to="/about" className={`nav-link ${pathname === "/about" ? "active" : ""}`}>
            About Us
          </Link>
          <Link to="/howtouse" className={`nav-link ${pathname === "/howtouse" ? "active" : ""}`}>
            How to Use
          </Link>

          {!currentUser && <Link to="/phoneauth" className="nav-link">Login/SignUp</Link>}

          {currentUser && (
            <div className="nav-user-block">
              {/* Notification */}
              <div ref={notificationRefDesktop} className="notification-wrapper">
                <button className="nav-notification" onClick={toggleNotifications}>
                  🔔
                  {unreadCount > 0 && (
                    <span className="notification-badge">
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </button>

                {showNotifications && (
                  <div className="notification-dropdown">
                    <NotificationList
                      onNotificationClick={handleNotificationClick}
                      onClose={() => setShowNotifications(false)}
                    />
                  </div>
                )}
              </div>

              {/* Avatar */}
              <Link to="/profile" className="nav-user-link">
                <div className="nav-avatar">
                  {avatarUrl && !imgError ? (
                    <img src={avatarUrl} alt="User" onError={() => setImgError(true)} />
                  ) : (
                    <span>{initials(currentUser?.name || currentUser?.email)}</span>
                  )}
                </div>
              </Link>
            </div>
          )}
        </div>

        {/* MOBILE MENU BUTTON */}
        <button className="hamburger" onClick={toggleMenu}>
          <span className="bar"></span>
          <span className="bar"></span>
          <span className="bar"></span>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="mobile-menu-overlay" onClick={() => setMenuOpen(false)}>
          <div className="mobile-menu" onClick={(e) => e.stopPropagation()}>
            <Link to="/" onClick={() => setMenuOpen(false)}>Home</Link>
            <Link to="/post" onClick={() => setMenuOpen(false)}>Post Ride</Link>
            <Link to="/about" onClick={() => setMenuOpen(false)}>About Us</Link>
            <Link to="/howtouse" onClick={() => setMenuOpen(false)}>How to Use</Link>

            {!currentUser && (
              <Link to="/phoneauth" onClick={() => setMenuOpen(false)}>Login/SignUp</Link>
            )}

            {currentUser && (
              <>
                <button className="nav-notification mobile-notification" onClick={toggleNotifications}>
                  🔔
                  {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
                </button>

                {showNotifications && (
                  <div ref={notificationRefMobile} className="mobile-notification-dropdown">
                    <NotificationList
                      onNotificationClick={handleNotificationClick}
                      onClose={() => setShowNotifications(false)}
                    />
                  </div>
                )}

                <Link to="/profile" onClick={() => setMenuOpen(false)}>Profile</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}